import React, { useEffect, useMemo, useState, useCallback } from "react";
import DOMPurify from "dompurify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  getAllRequests,
  updateRequest,
} from "./../../../../services/requestsServices";
// ⬇️ Ajuste ce chemin si besoin
// import { addNotification } from "../../../components/Notification/NotificationManager";
const addNotification = (...args) => console.log("[notify]", ...args); // fallback console si non dispo

const STATUS_ORDER = ["Pending", "In Progress", "Completed"];
const PRIORITIES = ["Low", "Medium", "High"];

const statusStyles = {
  Pending:
    "bg-gradient-to-r from-rose-500 to-orange-500 text-white ring-1 ring-white/10",
  "In Progress":
    "bg-gradient-to-r from-sky-600 to-indigo-600 text-white ring-1 ring-white/10",
  Completed:
    "bg-gradient-to-r from-emerald-600 to-teal-600 text-white ring-1 ring-white/10",
};

const priorityStyles = {
  Low: "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200",
  Medium: "bg-amber-100 text-amber-700 ring-1 ring-amber-200",
  High: "bg-rose-100 text-rose-700 ring-1 ring-rose-200",
};

function stripHtml(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = DOMPurify.sanitize(html || "");
  return tmp.textContent || tmp.innerText || "";
}

function cycleStatus(current) {
  const i = STATUS_ORDER.indexOf(current);
  return STATUS_ORDER[(i + 1) % STATUS_ORDER.length];
}

/* =========================
   MODALE D'ÉDITION
========================= */
function RequestEditModal({ open, onClose, request, onSave }) {
  const [form, setForm] = useState({
    description: "",
    status: "Pending",
    priority: "Medium",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !request) return;
    setForm({
      description: request.description || "",
      status: request.status || "Pending",
      priority: request.priority || "Medium",
    });
  }, [open, request]);

  const handleSave = async () => {
    if (!request) return;
    setSaving(true);
    try {
      await onSave(request.id, {
        description: form.description,
        status: form.status,
        priority: form.priority,
      });
      // addNotification("Requête mise à jour.", "success");
    } catch (e) {
      console.error(e);
      // addNotification("Erreur lors de la mise à jour.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!open || !request) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-3xl bg-white dark:bg-slate-900 rounded-t-2xl sm:rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 sm:p-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500">
              Édition de la requête
            </p>
            <h3 className="text-lg sm:text-xl font-bold">
              {request.title || "Sans titre"}
            </h3>
            <p className="text-sm text-slate-500">
              {request.Associations?.nom_asso ?? "Association inconnue"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
          >
            Fermer
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Statut
              </label>
              <select
                className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              >
                {STATUS_ORDER.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Priorité
              </label>
              <select
                className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                value={form.priority}
                onChange={(e) =>
                  setForm((f) => ({ ...f, priority: e.target.value }))
                }
              >
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
              Description (rich text)
            </label>
            <ReactQuill value={form.description} onChange={(v) => setForm((f) => ({ ...f, description: v }))} />
          </div>
        </div>

        <div className="p-4 sm:p-6 border-t border-slate-200 dark:border-slate-800 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-xl text-white bg-gradient-to-r from-sky-600 to-indigo-600 hover:opacity-95 disabled:opacity-60"
          >
            {saving ? "Sauvegarde..." : "Sauvegarder"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================
   CARD REQUÊTE
========================= */
function RequestCard({ req, onEdit, onCycleStatus, draggable = true }) {
  const pendingPulse =
    req.status === "Pending" ? (
      <span className="relative flex h-3 w-3">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-60" />
        <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
      </span>
    ) : null;

  return (
    <div
      className="group rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm hover:shadow-md transition cursor-grab active:cursor-grabbing"
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", String(req.id));
        e.dataTransfer.effectAllowed = "move";
      }}
    >
      <div className="flex items-center gap-3">
        <img
          src={req.Associations?.logo_asso}
          onError={(e) => (e.currentTarget.style.visibility = "hidden")}
          alt={req.Associations?.nom_asso || "Association"}
          className="w-9 h-9 rounded-full ring-1 ring-slate-200 dark:ring-slate-700 object-cover"
        />
        <div className="min-w-0">
          <p className="text-sm text-slate-500 truncate">
            {req.Associations?.nom_asso || "Association inconnue"}
          </p>
          <h4 className="font-semibold text-slate-900 dark:text-white">
            {req.title || "Sans titre"}
          </h4>
        </div>
      </div>

      <p className="mt-3 text-sm text-slate-600 dark:text-slate-300 line-clamp-3">
        {stripHtml(req.description).slice(0, 220)}
        {stripHtml(req.description).length > 220 ? "…" : ""}
      </p>

      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCycleStatus(req);
            }}
            className={`flex items-center gap-2 px-2.5 py-1 rounded-lg text-xs font-semibold ${statusStyles[req.status]}`}
            title="Changer le statut"
          >
            {pendingPulse}
            <span>{req.status}</span>
          </button>

          <span
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${priorityStyles[req.priority || "Medium"]}`}
            title="Priorité"
          >
            {req.priority || "Medium"}
          </span>
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit(req);
          }}
          className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-sm"
        >
          Modifier
        </button>
      </div>
    </div>
  );
}

/* =========================
   BARRE DE FILTRES
========================= */
function FiltersBar({
  requests,
  filterAsso,
  setFilterAsso,
  search,
  setSearch,
  priority,
  setPriority,
  showCompleted,
  setShowCompleted,
}) {
  const associations = useMemo(() => {
    const set = new Set(
      requests
        .map((r) => r.Associations?.nom_asso)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b))
    );
    return ["", ...Array.from(set)];
  }, [requests]);

  return (
    <div className="sticky top-0 z-10 -mx-4 sm:mx-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-b border-slate-200 dark:border-slate-800">
      <div className="p-4 grid grid-cols-1 lg:grid-cols-6 gap-3">
        <div className="lg:col-span-2">
          <label className="text-xs font-semibold text-slate-500">
            Association
          </label>
          <select
            className="w-full mt-1 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
            value={filterAsso}
            onChange={(e) => setFilterAsso(e.target.value)}
          >
            {associations.map((a) => (
              <option key={a || "ALL"} value={a}>
                {a ? a : "Toutes"}
              </option>
            ))}
          </select>
        </div>

        <div className="lg:col-span-2">
          <label className="text-xs font-semibold text-slate-500">Recherche</label>
          <input
            className="w-full mt-1 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
            placeholder="Titre ou description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-500">Priorité</label>
          <select
            className="w-full mt-1 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          >
            <option value="">Toutes</option>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-end">
          <label className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
            />
            <span>Afficher « Completed»</span>
          </label>
        </div>
      </div>
    </div>
  );
}

/* =========================
   COLONNE KANBAN
========================= */
function KanbanColumn({
  title,
  statusKey,
  items,
  onDropTo,
  highlight = false,
  collapsed = false,
  setCollapsed,
}) {
  return (
    <section
      className={`flex flex-col rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40 ${
        highlight ? "ring-2 ring-rose-400/40" : ""
      }`}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        const id = e.dataTransfer.getData("text/plain");
        if (id) onDropTo(Number(id), statusKey);
      }}
    >
      <header
        className={`px-4 py-3 rounded-t-2xl flex items-center justify-between ${
          statusStyles[title]
        }`}
      >
        <div className="flex items-center gap-2">
          <h3 className="font-semibold">{title}</h3>
          <span className="text-xs px-2 py-0.5 rounded-full bg-black/20">
            {items.length}
          </span>
        </div>
        {title === "Completed" && typeof collapsed === "boolean" && (
          <button
            onClick={() => setCollapsed?.((v) => !v)}
            className="text-xs px-2 py-1 rounded-lg bg-white/20 hover:bg-white/30"
          >
            {collapsed ? "Afficher" : "Masquer"}
          </button>
        )}
      </header>

      <div className="p-3 grid gap-3 auto-rows-min">
        {title === "Completed" && collapsed ? (
          <p className="text-xs text-slate-500 italic">
            Masqué par défaut pour désencombrer.
          </p>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500 italic p-3 bg-white/60 dark:bg-slate-900/60 rounded-xl border border-dashed border-slate-300 dark:border-slate-700">
            Glisse-dépose ici pour changer le statut.
          </div>
        ) : (
          items.map((r) => <React.Fragment key={r.id}>{r.render()}</React.Fragment>)
        )}
      </div>
    </section>
  );
}

/* =========================
   BOARD PRINCIPAL
========================= */
export default function RequestsBoard() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtres
  const [filterAsso, setFilterAsso] = useState("");
  const [search, setSearch] = useState("");
  const [priority, setPriority] = useState("");
  const [showCompleted, setShowCompleted] = useState(false); // ⬅️ masqué par défaut

  // Modale
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    const fetchRequests = async () => {
      setLoading(true);
      try {
        const data = await getAllRequests();
        setRequests(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Erreur lors du chargement des requêtes:", err);
        setRequests([]);
      } finally {
        setLoading(false);
      }
    };
    fetchRequests();
  }, []);

  const applyFilters = useCallback(
    (list) =>
      list.filter((req) => {
        if (filterAsso && req.Associations?.nom_asso !== filterAsso) return false;
        if (priority && (req.priority || "Medium") !== priority) return false;
        if (search) {
          const hay =
            (req.title || "") +
            " " +
            stripHtml(req.description || "") +
            " " +
            (req.Associations?.nom_asso || "");
          if (!hay.toLowerCase().includes(search.toLowerCase())) return false;
        }
        if (!showCompleted && req.status === "Completed") return false;
        return true;
      }),
    [filterAsso, priority, search, showCompleted]
  );

  const filtered = useMemo(() => applyFilters(requests), [applyFilters, requests]);

  const byStatus = useMemo(() => {
    const map = { Pending: [], "In Progress": [], Completed: [] };
    filtered.forEach((r) => {
      if (!map[r.status]) map[r.status] = [];
      map[r.status].push(r);
    });
    // tri léger: High > Medium > Low, puis récence (id desc supposé)
    const prioRank = { High: 0, Medium: 1, Low: 2 };
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => {
        const pa = prioRank[a.priority || "Medium"];
        const pb = prioRank[b.priority || "Medium"];
        if (pa !== pb) return pa - pb;
        return (b.id || 0) - (a.id || 0);
      });
    }
    return map;
  }, [filtered]);

  const optimisticUpdate = (id, patch) => {
    setRequests((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...patch } : r))
    );
  };

  const handleUpdate = async (id, patch) => {
    const before = requests.find((r) => r.id === id);
    optimisticUpdate(id, patch);
    try {
      await updateRequest(id, patch);
    } catch (e) {
      // revert
      optimisticUpdate(id, before || {});
      throw e;
    }
  };

  const handleCycleStatus = async (req) => {
    const next = cycleStatus(req.status);
    try {
      await handleUpdate(req.id, { status: next });
      // addNotification(`Statut → ${next}`, "success");
    } catch (e) {
      console.error(e);
      // addNotification("Impossible de changer le statut.", "error");
    }
  };

  const handleDropTo = async (id, toStatus) => {
    const req = requests.find((r) => r.id === id);
    if (!req || req.status === toStatus) return;
    try {
      await handleUpdate(id, { status: toStatus });
      // addNotification(`Déplacé vers « ${toStatus} »`, "success");
    } catch (e) {
      console.error(e);
      // addNotification("Échec du déplacement.", "error");
    }
  };

  const columns = useMemo(
    () => [
      {
        key: "Pending",
        title: "Pending",
        items: (byStatus.Pending || []).map((r) => ({
            id: r.id,
            render: () => (
              <RequestCard
                req={r}
                onEdit={(req) => setEditing(req)}
                onCycleStatus={handleCycleStatus}
              />
            ),
          })),
        highlight: true,
      },
      {
        key: "In Progress",
        title: "In Progress",
        items: (byStatus["In Progress"] || []).map((r) => ({
            id: r.id,
            render: () => (
              <RequestCard
                req={r}
                onEdit={(req) => setEditing(req)}
                onCycleStatus={handleCycleStatus}
              />
            ),
          })),
      },
      {
        key: "Completed",
        title: "Completed",
        items: (byStatus.Completed || []).map((r) => ({
            id: r.id,
            render: () => (
              <RequestCard
                req={r}
                onEdit={(req) => setEditing(req)}
                onCycleStatus={handleCycleStatus}
                draggable={showCompleted} // si masqué par défaut, on évite de drag sans les voir
              />
            ),
          })),
      },
    ],
    [byStatus, showCompleted]
  );

  // état repli de la colonne Completed (indépendant du filtre principal)
  const [completedCollapsed, setCompletedCollapsed] = useState(true);
  useEffect(() => {
    setCompletedCollapsed(!showCompleted);
  }, [showCompleted]);

  return (
    <div className="max-w-[1400px] mx-auto">
      <h2 className="text-2xl font-bold mb-2">Requêtes clients</h2>
      <p className="text-slate-600 mb-4">
        Glisse-dépose une carte pour changer de colonne. Les complétées sont
        masquées par défaut.
      </p>

      {/* Filtres */}
      <FiltersBar
        requests={requests}
        filterAsso={filterAsso}
        setFilterAsso={setFilterAsso}
        search={search}
        setSearch={setSearch}
        priority={priority}
        setPriority={setPriority}
        showCompleted={showCompleted}
        setShowCompleted={setShowCompleted}
      />

      {/* Skeleton loading */}
      {loading ? (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4">
              <div className="h-6 w-40 rounded bg-slate-200 dark:bg-slate-800 mb-3" />
              {[...Array(4)].map((__, j) => (
                <div
                  key={j}
                  className="h-24 rounded-xl bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 mb-3"
                />
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {columns.map((col) => (
            <KanbanColumn
              key={col.key}
              title={col.title}
              statusKey={col.key}
              items={col.items}
              onDropTo={handleDropTo}
              highlight={!!col.highlight}
              collapsed={col.key === "Completed" ? completedCollapsed : undefined}
              setCollapsed={col.key === "Completed" ? setCompletedCollapsed : undefined}
            />
          ))}
        </div>
      )}

      {/* Modale */}
      <RequestEditModal
        open={!!editing}
        request={editing}
        onClose={() => setEditing(null)}
        onSave={async (id, patch) => {
          await handleUpdate(id, patch);
          setEditing(null);
        }}
      />
    </div>
  );
}
